/**
 * AuthGate — gates routes behind a valid OIDC session.
 *
 * Logik 1:1 von InvoiceAI uebernommen
 * (../../invoiceai/frontend/src/hooks/use-auth.ts):
 *
 * 1. Nicht authentifiziert + frisch vom Hub zurück → Silent-Signin
 *    (Keycloak-SSO-Session konsumieren).
 * 2. Nicht authentifiziert + erste Anmeldung → Redirect zum Hub.
 * 3. Authentifiziert, aber keine Memberships → Hub-Onboarding.
 * 4. Authentifiziert + Memberships → render children.
 */
import { useEffect, useRef } from "react";
import { useAuthContext } from "@/contexts/AuthContext";
import { redirectToHub, userManager } from "@/lib/auth";
import { Skeleton } from "@/components/ui/skeleton";

const HUB_ROUNDTRIP_FLAG = "paypilot_hub_roundtrip_pending";

function LoadingScreen() {
  return (
    <div className="flex h-full min-h-screen items-center justify-center p-8">
      <div className="w-full max-w-md space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  );
}

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { isLoading, isAuthenticated, memberships } = useAuthContext();
  const silentInFlight = useRef(false);

  useEffect(() => {
    if (isLoading) return;
    if (window.location.pathname.startsWith("/auth/")) return;

    if (!isAuthenticated) {
      if (silentInFlight.current) return;

      const justReturnedFromHub =
        sessionStorage.getItem(HUB_ROUNDTRIP_FLAG) === "true";

      if (justReturnedFromHub) {
        sessionStorage.removeItem(HUB_ROUNDTRIP_FLAG);
        silentInFlight.current = true;
        // Full redirect statt iframe-basiertem signinSilent: Keycloak's CSP
        // (frame-ancestors 'self') blockt das iframe-Renewal von anderen
        // Origins ausser wenn die Domain in den Web-Origins des Keycloak-
        // Clients steht. Voller signinRedirect funktioniert immer — die
        // bestehende SSO-Session wird ohne neue Login-Maske durchgereicht.
        userManager
          .signinRedirect({
            state:
              window.location.pathname +
              window.location.search +
              window.location.hash,
          })
          .catch(() => {
            redirectToHub();
          });
        return;
      }

      sessionStorage.setItem(HUB_ROUNDTRIP_FLAG, "true");
      redirectToHub();
      return;
    }

    if (memberships.length === 0) {
      redirectToHub();
    }
  }, [isLoading, isAuthenticated, memberships]);

  if (isLoading || !isAuthenticated || memberships.length === 0) {
    return <LoadingScreen />;
  }
  return <>{children}</>;
}
